import ExcludeIcon from '@/assets/icons/exclude.svg?react';
import { T } from '@/components/i18n/message.i18n.component';
import Button from '@/components/ui/button/button.component';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs.component';
import { ProductBuildingBlock } from '@/gql/graphql';
import { pretailoringPageMessages } from '@/pages/products/pre-tailoring/pre-tailoring.messages';
import { tailoringMessages } from '@/pages/products/tailoring/tailoring.messages';
import { isSelectedBBIncluded } from '@/pages/products/tailoring/tailoring.page.core';
import { useIntl } from 'react-intl';

type BuildingBlockTitleProps = {
  bb?: ProductBuildingBlock;
  handleBBInclusion: (bb: ProductBuildingBlock) => void;
  tailoringEnabled: boolean;
  // passed from the tailoring layout
  height?: number;
  bbTab: 'general-details' | 'information' | 'data-blocks' | 'design-obligation';
  setBBTab: (tab: 'general-details' | 'information' | 'data-blocks' | 'design-obligation') => void;
};

export const BuildingBlockTitle = ({
  bb,
  bbTab,
  tailoringEnabled,
  handleBBInclusion,
  setBBTab,
}: BuildingBlockTitleProps) => {
  const t = useIntl();

  if (!bb) return null;

  return (
    <div className="bg-light-gray px-4 pt-4 flex flex-col">
      <div className="flex flex-row justify-between items-center">
        <h2 className="mb-4">
          {t.formatMessage(tailoringMessages.bbSectionTitle, {
            name: bb?.name,
          })}
        </h2>
        {tailoringEnabled && (
          <Button
            className="mb-4"
            variant="secondary"
            onClick={() => {
              handleBBInclusion(bb);
            }}
          >
            {isSelectedBBIncluded(bb) ? (
              <>
                <ExcludeIcon className="mr-2" />
                <T {...tailoringMessages.exclude} />
              </>
            ) : (
              <T {...tailoringMessages.include} />
            )}
          </Button>
        )}
      </div>
      <div className="bg-white px-4">
        <Tabs
          value={bbTab}
          onValueChange={(tab) => {
            setBBTab(tab as 'general-details' | 'information' | 'data-blocks' | 'design-obligation');
          }}
          className="flex flex-col"
        >
          <div className="bg-white pt-4">
            <TabsList className="mb-5" data-testid="building-block-details-tabs-list">
              <TabsTrigger
                value="general-details"
                data-testid="building-block-details-tabs-general-details-tab-trigger"
              >
                <p className="typo-primary-500">
                  <T {...pretailoringPageMessages.singleBuildingBlockTabGeneralDetails} />
                </p>
              </TabsTrigger>
              <TabsTrigger value="information" data-testid="building-block-details-tabs-information-tab-trigger">
                <p className="typo-primary-500">
                  <T {...pretailoringPageMessages.singleBuildingBlockTabInformation} />
                </p>
              </TabsTrigger>
              <TabsTrigger value="data-blocks" data-testid="building-block-details-tabs-data-blocks-tab-trigger">
                <p className="typo-primary-500">
                  <T {...pretailoringPageMessages.singleBuildingBlockTabDataBlocks} />
                </p>
              </TabsTrigger>
              <TabsTrigger
                value="design-obligation"
                data-testid="building-block-details-tabs-design-obligation-tab-trigger"
              >
                <p className="typo-primary-500">
                  <T {...pretailoringPageMessages.singleBuildingBlockTabDesignObligation} />
                </p>
              </TabsTrigger>
            </TabsList>
          </div>
        </Tabs>
      </div>
    </div>
  );
};
